<template>
    <div class="title">
        說明關係及原因
    </div>
    <div class="content xs:max-w-[32.5rem]">
        <transition
            name="opacity"
            mode="out-in"
        >
            <div
                v-if="isLoading"
                class="w-[90vw] max-w-full"
            >
                <SkeletonBase :content="1" />
                <SkeletonBase
                    hasTitle
                    :content="1"
                />
                <SkeletonBase :content="4" />
            </div>
            <div v-else>
                <ul class="text-tip mb-2">
                    <li>以下行動電話經核實後非為本人所申辦，請輸入與門號登記人關係</li>
                </ul>
                <template
                    v-for="(item,idx) in userData"
                    :key="idx"
                >
                    <div
                        v-if="item.isShow"
                        class="border-b border-dashed border-gray-100 last:border-none mb-2.5 last:mb-0"
                    >
                        <h4 class="mb-1">
                            {{ item.id ==='applicant'?'要保人行動電話' :'' }}
                            {{ item.id ==='insured'?'被保險人行動電話' :'' }}
                        </h4>
                        <div class="text-base xs:text-lg text-gray-900 mb-1">
                            {{ item.mobile }}
                        </div>
                        <label class="label px-0 w-max cursor-default">
                            <span class="text-gray-700">與門號登記人關係</span>
                        </label>
                        <div class="mr-4 mb-1.5">
                            <Multiselect
                                v-model="item.relationship"
                                class="select-base ml-0.5"
                                placeholder="請選取關係"
                                :options="item.relations"
                                :can-clear="false"
                                :can-deselect="false"
                                @select="item.errorMsg.relationship = ''"
                            />
                            <p class="text-error text-right">
                                {{ item.errorMsg.relationship }}
                            </p>
                        </div>
                        <label
                            class="label px-0 w-max text-gray-700"
                            for="textareaApplicant"
                        >
                            說明關係及原因
                        </label>
                        <div class="mr-4 mb-2">
                            <textarea
                                id="textareaApplicant"
                                v-model.trim="item.reason"
                                class="textarea textarea-base ml-0.5"
                                placeholder="請說明關係及原因"
                                autocomplete="off"
                                @input="validateReason(item)"
                            />
                            <p class="text-error text-right">
                                {{ item.errorMsg.reason }}
                            </p>
                        </div>
                    </div>
                </template>
            </div>
        </transition>
    </div>
    <div class="action justify-between items-center">
        <button
            class="btn-text arrow-l-dark h-6 text-gray-900"
            @click.stop="$emit('goTelChange')"
        >
            修改行動電話
        </button>
        <button
            class="btn gradient-primary rounded-full"
            :disabled="isSpin"
            @click.stop="validate()"
        >
            <SvgIcon
                v-if="isSpin"
                icon-name="loading"
                class="animate-spin h-6 w-6"
            />
            <span v-else>確定</span>
        </button>
    </div>
</template>

<script>
import { ref } from 'vue';
import Multiselect from '@vueform/multiselect';
import { getMidRelationInfo, postMidRelationInfo } from '@/apis/mid.js';
import SkeletonBase from '@/components/skeletons/SkeletonBase.vue';

export default {
    components: {
        Multiselect,
        SkeletonBase
    },
    emits: ['goTelChange', 'isPass', 'result'],
    setup(props, { emit }) {
        const isLoading = ref(false);
        const isSpin = ref(false);
        const userData = ref([
            {
                id: 'applicant',
                mobile: '',
                reason: '',
                relationship: '',
                errorMsg: {
                    relationship: '',
                    reason: ''
                }
            }, {
                id: 'insured',
                mobile: '',
                reason: '',
                relationship: '',
                errorMsg: {
                    relationship: '',
                    reason: ''
                }
            }
        ]);

        const getData = () => {
            isLoading.value = true;
            getMidRelationInfo().then(res => {
                if (res.data) {
                    const { mobile, needUpdateRelation, insuredMobile, needUpdateInsuredRelation, relations } = res.data;
                    userData.value = userData.value.map(i => {
                        if (i.id === 'applicant') {
                            i.mobile = mobile;
                            /* 需要填寫與要保人門號核申辦人關係就出現 */
                            i.isShow = needUpdateRelation;
                        }
                        if (i.id === 'insured') {
                            i.mobile = insuredMobile;
                            /* 需要填寫與被保人門號核申辦人關係就出現 */
                            i.isShow = needUpdateInsuredRelation;
                        }
                        i.relations = relations.map(i => {
                            i.label = i.text;
                            return i;
                        });
                        return i;
                    });
                }
            }).finally(() => {
                isLoading.value = false;
            });
        };
        getData();

        const validateReason = (value) => {
            value.errorMsg.reason = value.reason.length > 50 || value.reason.length < 1 ? '請輸入1至50個字' : '';
        };
        const validate = () => {
            userData.value.forEach(i => {
                /* 有出現才檢核是否未填 */
                if (i.isShow) {
                    if (!i.relationship) i.errorMsg.relationship = '您未填寫與門號登記人之關係，請確認後填寫。';
                    if (!i.reason) i.errorMsg.reason = '要/被保險人行動電話非本人所申辦，請填寫說明關係及原因。';
                }
            });
            const hasError = userData.value.some(i => i.errorMsg.relationship !== '' || i.errorMsg.reason !== '');
            if (!hasError) {
                isSpin.value = true;
                const returnData = {};
                userData.value.forEach(i => {
                    if (i.id === 'applicant' && i.isShow) {
                        returnData.relationCode = i.relationship;
                        returnData.relationMemo = i.reason;
                    }
                    if (i.id === 'insured' && i.isShow) {
                        returnData.insuredRelationCode = i.relationship;
                        returnData.insuredRelationMemo = i.reason;
                    }
                });
                postMidRelationInfo(returnData).then(res => {
                    emit('result', { type: res.data.type, target: res.data.target });
                    emit('isPass', res.data.type === 'A'); // there are B & C
                }).finally(() => {
                    isSpin.value = false;
                });
            }
        };

        return {
            isLoading,
            isSpin,
            userData,
            validateReason,
            validate
        };
    }
};
</script>
